
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddBoxIcon from "@mui/icons-material/AddBox";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import {
  Box,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
} from "@mui/material";

import SmallSearch from "../Search/SmallSearch";
import { addInventory, restockInventory } from "../../actions/inventory";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    minWidth: {md:600},
    
  },
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2, 3),
  },
}));

function a11yProps(index) {
  return { id: `stock-tab-${index}`, "aria-controls": `stock-tabpanel-${index}` };
}

export default function AddStockDialog({ externalMode, restockRow, onClose }) {
  const dispatch = useDispatch();

  const inventory = useSelector((state) => state.inventory?.list) || [];
  const member = useSelector((state) => state.user);
  const pharmacyId = member?.id;

  const [open, setOpen] = React.useState(false);

  // UI mode
  const [mode, setMode] = React.useState("ADD_NEW"); // ADD_NEW | RESTOCK
  const [tab, setTab] = React.useState(0); // 0=Medicine, 1=Product

  // shared fields
  const [supplier, setSupplier] = React.useState("");
  const [units, setUnits] = React.useState("");
  const [batchNo, setBatchNo] = React.useState("");
  const [expiryDate, setExpiryDate] = React.useState("");
  const [wholesalePrice, setWholesalePrice] = React.useState("");
  const [retailPrice, setRetailPrice] = React.useState("");
  const [discount, setDiscount] = React.useState("");
  const [strength, setStrength] = React.useState("");
  const [lowStockThreshold, setLowStockThreshold] = React.useState("");
  const [criticalThreshold, setCriticalThreshold] = React.useState("");
  const [lowMode, setLowMode] = React.useState("RECOMMENDED");   // or "CUSTOM"
  const [critMode, setCritMode] = React.useState("RECOMMENDED");

  // medicine fields
  const [medicineId, setMedicineId] = React.useState("");
  const [medicineName, setMedicineName] = React.useState("");

  // product fields
  const [itemName, setItemName] = React.useState("");
  const [trackExpiry, setTrackExpiry] = React.useState(false);

  // batch match handling (restock prompt)
  const [pendingBatchMatch, setPendingBatchMatch] = React.useState(null);
  const [showBatchConfirm, setShowBatchConfirm] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [showRequirements, setShowRequirements] = React.useState(false);

  // anchor for the popover
  const [requirementsAnchorEl, setRequirementsAnchorEl] = React.useState(null);

  const isDetailsLocked = mode === "RESTOCK" || showBatchConfirm;

  const refs = {
    batch: React.useRef(),
    units: React.useRef(),
    expiry: React.useRef(),
    supplier: React.useRef(),
    strength: React.useRef(),
    wholesale: React.useRef(),
    retail: React.useRef(),
    discount: React.useRef(),
    lowThreshold: React.useRef(),     
    criticalThreshold: React.useRef(),
  };

  const handleEnterNext = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus();
    }
  };

  const sharedFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1,
      backgroundColor: "#f3f3f6",
      "& fieldset": { border: "none" },
      "& input": {
        color: "#333",
        fontSize: "1rem",
        fontWeight: 500,
        "&::placeholder": { color: "#888", opacity: 1,
          fontSize: { xs: '0.85rem', md: '1rem' },
         },
      },
    },
  };

  const baseLabelSx = { display: "block", mb: 1, fontWeight: 500 };

  // --- helpers to read backend shape ---
  const getRowItem = (inv) => inv?.itemId || {};
  // const isMedicineRow = (inv) => getRowItem(inv)?.type === "MEDICINE";
  
  // const isProductRow = (inv) => getRowItem(inv)?.type === "PRODUCT";
  
  // const getRowMedicine = (inv) => getRowItem(inv)?.medicineId || null;
  // const getRowProductName = (inv) => getRowItem(inv)?.name || "";

  // const getRowDisplayName = (inv) => {
  //   if (!inv) return "";
  //   if (isProductRow(inv)) return getRowProductName(inv);
  //   const med = getRowMedicine(inv);
  //   return med?.brandName || "";
  // };

  // const getRowGeneric = (inv) => {
  //   const med = getRowMedicine(inv);
  //   return med?.genericName || "";
  // };

  // Find matching inventory row for (medicineId + batch) OR (itemName + batch)
  const checkExisting = ({ medicineId: mId, itemName: pName, batchNo: bNo }) => {
    const bn = String(bNo || "").trim().toLowerCase();
    if (!bn) return null;

    return inventory.find((inv) => {
      const item = getRowItem(inv);
      const invBatch = String(inv?.batchNo || "").trim().toLowerCase();
      if (invBatch !== bn) return false;

      if (mId) {
        const med = item?.medicineId;
        const id = med?._id ?? med;
        return item?.type === "MEDICINE" && String(id) === String(mId);
      }

      if (pName) {
        const name = String(item?.name || "").trim().toLowerCase();
        return item?.type === "PRODUCT" && name === String(pName).trim().toLowerCase();
      }

      return false;
    });
  };

  const resetAll = () => {
    setMode("ADD_NEW");
    setTab(0);

    setSupplier("");
    setUnits("");
    setBatchNo("");
    setExpiryDate("");
    setWholesalePrice("");
    setRetailPrice("");
    setDiscount("");
    setStrength("");

    setLowStockThreshold("");
    setCriticalThreshold("");
    setLowMode("RECOMMENDED");
    setCritMode("RECOMMENDED");

    setMedicineId("");
    setMedicineName("");

    setItemName("");
    setTrackExpiry(false);
    // setTrackBatch(false);

    setPendingBatchMatch(null);
    setShowBatchConfirm(false);
  };

  // Open from outside as RESTOCK
  React.useEffect(() => {
    if (externalMode === "RESTOCK" && restockRow) {
      setMode("RESTOCK");
      setOpen(true);

      if (restockRow?.itemType === "PRODUCT") setTab(1);
      else setTab(0);

      if (restockRow?.itemType === "PRODUCT") {
        setItemName(restockRow?.itemName || restockRow?.medicineName || "");
      } else {
        setMedicineId(restockRow?.medicineId || "");
        setMedicineName(restockRow?.medicineName || "");
      }

      setBatchNo(restockRow?.batchNumber || "");
      setStrength(restockRow?.strength || "");
      setSupplier(restockRow?.supplier || "");

      setExpiryDate(
        restockRow?.expiryDateRaw ? restockRow.expiryDateRaw.toISOString().split("T")[0] : ""
      );

      setWholesalePrice(String(restockRow?.wholesalePrice ?? ""));
      setRetailPrice(String(restockRow?.retailPrice ?? ""));
      setDiscount(String(restockRow?.discount ?? ""));

      setLowStockThreshold(
        restockRow?.lowStockThreshold != null
          ? String(restockRow.lowStockThreshold)
          : ""
      );
      setCriticalThreshold(
        restockRow?.criticalThreshold != null
          ? String(restockRow.criticalThreshold)
          : ""
      );

      setLowMode("RECOMMENDED");
      setCritMode("RECOMMENDED");

      setUnits("");

      setPendingBatchMatch({ _id: restockRow?.id || restockRow?._id });
      setShowBatchConfirm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMode, restockRow]);

  const handleClickOpen = () => {
    resetAll();
    setErrorMsg("");
    setShowRequirements(false);
    setRequirementsAnchorEl(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetAll();
    setErrorMsg("");
    setShowRequirements(false);
    setRequirementsAnchorEl(null);
    if (onClose) onClose();
  };

  const handleSearchSelect = ({ id, name }) => {
    setMedicineId(id);
    setMedicineName(name);

    setMode("ADD_NEW");
    setPendingBatchMatch(null);
    setShowBatchConfirm(false);
  };

  const handleTabChange = (e, newValue) => {
    if (isDetailsLocked) return;

    setTab(newValue);
    setPendingBatchMatch(null);
    setShowBatchConfirm(false);

    setMedicineId("");
    setMedicineName("");
    setItemName("");
  };

  const handleBatchChange = (val) => {
  setBatchNo(val);

  if (!String(val || "").trim()) {
    setShowBatchConfirm(false);
    setPendingBatchMatch(null);
  }
};


  React.useEffect(() => {
  if (mode === "RESTOCK") return;

  const bn = String(batchNo || "").trim();
  if (!bn) {
    setShowBatchConfirm(false);
    setPendingBatchMatch(null);
    return;
  }

  const hasIdentity =
    (tab === 0 && String(medicineId || "").trim()) ||
    (tab === 1 && String(itemName || "").trim());

  if (!hasIdentity) {
    setShowBatchConfirm(false);
    setPendingBatchMatch(null);
    return;
  }

  const payload =
    tab === 0
      ? { medicineId, itemName: null, batchNo: bn }
      : { medicineId: null, itemName, batchNo: bn };

  const match = checkExisting(payload);

  if (match) {
    setPendingBatchMatch(match);
    setShowBatchConfirm(true);
  } else {
    setPendingBatchMatch(null);
    setShowBatchConfirm(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [batchNo, medicineId, itemName, tab, mode]);


  const handleRestockMode = () => {
    if (!pendingBatchMatch) return;

    setMode("RESTOCK");
    setShowBatchConfirm(false);

    setStrength(pendingBatchMatch?.strength || "");
    setSupplier(pendingBatchMatch?.supplier || "");

    if (pendingBatchMatch?.expiryDate) {
      const d = new Date(pendingBatchMatch.expiryDate);
      if (!Number.isNaN(d.getTime())) setExpiryDate(d.toISOString().split("T")[0]);
    } else {
      setExpiryDate("");
    }

    if (pendingBatchMatch?.wholesalePrice != null)
      setWholesalePrice(String(pendingBatchMatch.wholesalePrice));
    if (pendingBatchMatch?.retailPrice != null)
      setRetailPrice(String(pendingBatchMatch.retailPrice));
    if (pendingBatchMatch?.discount != null)
      setDiscount(String(pendingBatchMatch.discount));

    if (pendingBatchMatch?.lowStockThreshold != null) {
  setLowStockThreshold(String(pendingBatchMatch.lowStockThreshold));
    } else {
      setLowStockThreshold("");
    }
    if (pendingBatchMatch?.criticalThreshold != null) {
      setCriticalThreshold(String(pendingBatchMatch.criticalThreshold));
    } else {
      setCriticalThreshold("");
    }


    const item = getRowItem(pendingBatchMatch);
    if (item?.type === "PRODUCT") {
      setTab(1);
      setItemName(item?.name || "");
    } else {
      const med = item?.medicineId;
      setTab(0);
      setMedicineId(med?._id ?? "");
      setMedicineName(med?.brandName ?? "");
    }
  };

  const handleAdd = async () => {
    try {
      setErrorMsg("");

      if (mode === "RESTOCK") {
        const restockId = pendingBatchMatch?._id || restockRow?.id;
        if (!restockId) return;

        await dispatch(restockInventory({ inventoryId: restockId, units }));
        handleClose();
        return;
      }

            const payload = {
        pharmacyId,
        supplier,
        units,
        wholesalePrice,
        retailPrice,
        discount,
        strength,
        batchNo: String(batchNo || "").trim(),
        expiryDate: String(expiryDate || "").trim() || null,
      };

      // decide what to send for low / critical thresholds
let lowToSend = null;
let critToSend = null;

// LOW
if (lowMode === "RECOMMENDED") {
  if (hasUnitsNum && recommendedLowUnits) {
    lowToSend = recommendedLowUnits; // already whole + ≥ 2
  }
} else {
  const lowTrim = String(lowStockThreshold || "").trim();
  if (lowTrim !== "") {
    const raw = Number(lowStockThreshold);
    if (Number.isFinite(raw)) {
      const rounded = Math.round(raw);
      // min 2 units for low
      lowToSend = Math.max(2, rounded);
    }
  }
}

// CRITICAL
if (critMode === "RECOMMENDED") {
  if (hasUnitsNum && recommendedCritUnits) {
    critToSend = recommendedCritUnits; // already whole + ≥ 1
  }
} else {
  const critTrim = String(criticalThreshold || "").trim();
  if (critTrim !== "") {
    const raw = Number(criticalThreshold);
    if (Number.isFinite(raw)) {
      const rounded = Math.round(raw);
      // min 1 unit for critical
      critToSend = Math.max(1, rounded);
    }
  }
}


if (lowToSend != null && critToSend != null) {
  // critical must be strictly less than low
  critToSend = Math.min(critToSend, lowToSend - 1);
  critToSend = Math.max(1, critToSend);
}


payload.lowStockThreshold = lowToSend;
payload.criticalThreshold = critToSend;



      if (tab === 0) {
        payload.medicineId = medicineId;
      } else {
        payload.itemName = itemName;
        payload.trackExpiry = Boolean(trackExpiry);
        // payload.trackBatch = Boolean(trackBatch);
      }

      await dispatch(addInventory(payload));
      handleClose();
    } catch (error) {
      console.error("Failed to add/restock:", error);

      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";

      setErrorMsg(msg);
    }
  };

  // --- validation state derived from current form values ---
  const hasMedicine = tab === 0 && String(medicineName || "").trim();
  const hasProduct = tab === 1 && String(itemName || "").trim();
  const needBatch = true;
  const needExpiry = tab === 0 || (tab === 1 && trackExpiry);
  const q = Number(units);
  const w = Number(wholesalePrice);
  const r = Number(retailPrice);

  // 🆕 recommended thresholds based on units (20% / 10%)
  const hasUnitsNum =
    String(units || "").trim() && Number.isFinite(q) && q > 0;
  // 🧮 Recommended thresholds (always whole numbers, with mins)
  // Low: at least 2 units
  // Critical: at least 1 unit
  const recommendedLowUnits  = hasUnitsNum ? Math.max(2, Math.round(q * 0.2)) : "";
  const recommendedCritUnits = hasUnitsNum ? Math.max(1, Math.round(q * 0.1)) : "";


  const showLowPct  = hasUnitsNum && q >= 10;
  const showCritPct = hasUnitsNum && q >= 10;

  const rawLow = q * 0.2;
  const rawCrit = q * 0.1;

  const lowIsApprox  = hasUnitsNum && !Number.isInteger(rawLow);
  const critIsApprox = hasUnitsNum && !Number.isInteger(rawCrit);


  // custom threshold numbers (only relevant when in CUSTOM mode)
  const lowNum  = Number(lowStockThreshold);
  const critNum = Number(criticalThreshold);
  const hasLow  =
    lowMode === "CUSTOM" && String(lowStockThreshold || "").trim() !== "";
  const hasCrit =
    critMode === "CUSTOM" && String(criticalThreshold || "").trim() !== "";

  // ---- inline errors for CUSTOM thresholds (UI feedback) ----

// effective values (what the system will actually use)
const effectiveLow =
  lowMode === "CUSTOM" && String(lowStockThreshold || "").trim() !== ""
    ? Number(lowStockThreshold)
    : hasUnitsNum
    ? recommendedLowUnits
    : null;

// LOW
const lowCustomActive = lowMode === "CUSTOM";
const lowCustomHasVal = String(lowStockThreshold || "").trim() !== "";
const lowCustomNum    = Number(lowStockThreshold);

const lowMinError =
  lowCustomActive &&
  lowCustomHasVal &&
  (!Number.isFinite(lowCustomNum) || lowCustomNum < 2);

// CRITICAL
const critCustomActive = critMode === "CUSTOM";
const critCustomHasVal = String(criticalThreshold || "").trim() !== "";
const critCustomNum    = Number(criticalThreshold);

const critMinError =
  critCustomActive &&
  critCustomHasVal &&
  (!Number.isFinite(critCustomNum) || critCustomNum < 1);

const critVsLowError =
  critCustomActive &&
  critCustomHasVal &&
  effectiveLow != null &&
  Number.isFinite(critCustomNum) &&
  critCustomNum >= effectiveLow;




  const hasBothPrices =
    String(wholesalePrice || "").trim() &&
    String(retailPrice || "").trim();

  const hasBatch = String(batchNo || "").trim();

  const validationRules = [
    {
      key: "identity",
      label: tab === 0 ? "Medicine selected" : "Product name entered",
      ok: Boolean(hasMedicine || hasProduct),
      message: tab === 0 ? "Please select a medicine." : "Please enter a product name.",
    },
    {
      key: "units",
      label: "Units entered (> 0)",
      ok: String(units || "").trim() && Number.isFinite(q) && q > 0,
      message: "Units must be a number greater than 0.",
    },
    {
      key: "duplicateBatch",
      label: "Duplicate batch resolved (Restock or change batch number)",
      ok: !hasBatch || !showBatchConfirm,
      active: Boolean(hasBatch),
      message: "This batch already exists. Choose Restock or change batch number.",
    },
    {
      key: "batch",
      label: "Batch number filled",
      ok: !needBatch || String(batchNo || "").trim(),
      message: "Batch number is required.",
    },
    {
      key: "expiry",
      label: "Expiry date filled",
      ok: !needExpiry || String(expiryDate || "").trim(),
      message: "Expiry date is required.",
    },
    {
      key: "wholesale",
      label: "Wholesale price entered",
      ok: String(wholesalePrice || "").trim(),
      message: "Wholesale price is required.",
    },
    {
      key: "retail",
      label: "Retail price entered",
      ok: String(retailPrice || "").trim(),
      message: "Retail price is required.",
    },
    {
      key: "margin",
      label: "Retail price ≥ wholesale price",
      ok:
        !hasBothPrices ||
        (!Number.isNaN(w) && !Number.isNaN(r) && r >= w),
      active: Boolean(hasBothPrices),
      message: "Retail price must be greater than or equal to wholesale price.",
    },
    {
  key: "lowThresholdValid",
  label: "Low stock alert is a valid number (≥ 2 units)",
  ok: !hasLow || (!Number.isNaN(lowNum) && lowNum >= 2),
  active: hasLow,
  message: "Low stock alert must be at least 2 units.",
},
{
  key: "critThresholdValid",
  label: "Critical alert is a valid number (≥ 1 unit)",
  ok: !hasCrit || (!Number.isNaN(critNum) && critNum >= 1),
  active: hasCrit,
  message: "Critical alert must be at least 1 unit.",
},
{
  key: "critNotGreaterThanLow",
  label: "Critical alert value must be less than low stock alert value",
  ok: !hasLow || !hasCrit || critNum < lowNum,
  active: hasLow && hasCrit,
  message: "Critical alert cannot be greater than low-stock alert.",
},

  ];

  const firstFailedRule = validationRules.find((rr) => !rr.ok);
  const disableReason = firstFailedRule?.message || "";
  const isDisabled = Boolean(disableReason);

  const titleText = mode === "RESTOCK" ? "Restock" : "Add Stock";

  const requirementsOpen =
    Boolean(requirementsAnchorEl) && showRequirements && isDisabled;

  return (
    <React.Fragment>
      <Button
        variant="contained"
        size="large"
        onClick={handleClickOpen}
        startIcon={<AddBoxIcon sx={{ color: "white" }} />}
        sx={{ textTransform: "none", backgroundColor: "#1976d2", boxShadow: "none" }}
      >
        Add Stock
      </Button>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="add-stock-dialog-title"
        open={open}
      >
        <DialogTitle
          sx={{ m: 0, p: 2, fontSize: "1.25rem", fontWeight: 600 }}
          id="add-stock-dialog-title"
        >
          {titleText}
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          {mode === "RESTOCK" && (
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#eef6ff",
                border: "1px solid #1976d2",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#1976d2", fontWeight: 500, textAlign: "center" }}
              >
                You are restocking an existing batch. Only the quantity can be changed.
              </Typography>
            </Box>
          )}

          {/* Tabs */}
          <Box sx={{ mb: 2 }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                background: "#f3f3f6",
                borderRadius: "10px",
                px: 0.5,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 44 },
              }}
            >
              <Tab label="Medicine" {...a11yProps(0)} />
              <Tab label="Other Product" {...a11yProps(1)} />
            </Tabs>
          </Box>

          {/* Identity section */}
          <Grid container spacing={4}>
            <Grid style={{ width: "100%" }}>
              <Typography
                component="label"
                sx={{ ...baseLabelSx, color: isDetailsLocked ? "darkgray" : "inherit" }}
              >
                {tab === 0 ? "Medicine Name" : "Product Name"}
              </Typography>

              {isDetailsLocked ? (
                <Typography
                  sx={{ color: "gray", fontWeight: 600, fontSize: "1rem", mt: 0.25 }}
                >
                  {tab === 0 ? medicineName || "—" : itemName || "—"}
                </Typography>
              ) : tab === 0 ? (
                <SmallSearch fullWidth onSelect={handleSearchSelect} />
              ) : (
                <TextField
                  placeholder="e.g. Gloves / Syringe / Vitamin C / Bandage"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={itemName}
                  onChange={(e) => {
                    setItemName(e.target.value);
                    setPendingBatchMatch(null);
                    setShowBatchConfirm(false);
                  }}
                  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();

        refs.batch?.current?.focus();
    }
  }}
                  sx={sharedFieldSx}
                />
              )}

              {tab === 1 && !isDetailsLocked && (
                <Box sx={{ mt: 1.5, display: "flex", gap: 2, alignItems: "center" }}>
                  {/* <FormControlLabel
                    control={
                      <Switch
                        checked={trackBatch}
                        onChange={(e) => setTrackBatch(e.target.checked)}
                      />
                    }
                    label="Track Batch"
                    sx={{ userSelect: "none" }}
                  /> */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={trackExpiry}
                        onChange={(e) => setTrackExpiry(e.target.checked)}
                      />
                    }
                    label="Track Expiry"
                    sx={{ userSelect: "none" }}
                  />
                </Box>
              )}
            </Grid>

            {/* Batch Number */}
            <Grid container style={{ width: "100%", justifyContent: "space-between" }}>
              <Grid item sx={{ width: {xs:'100%', md:'100%'} }}>
                <Typography
                  component="label"
                  sx={{
                    ...baseLabelSx,
                    color: isDetailsLocked ? "darkgray" : "inherit",
                  }}
                >
                  Batch Number
                </Typography>

                <TextField
                  placeholder="Enter batch number"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={batchNo}
                  disabled={mode === "RESTOCK"}
                  onChange={(e) => handleBatchChange(e.target.value)}
                  inputRef={refs.batch}
                  onKeyDown={(e) => handleEnterNext(e, refs.units)}
                  sx={sharedFieldSx}
                  style={{width:'45%'}}
                />

                {showBatchConfirm && (
                  <Box
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      padding: "15px",
                      marginTop: "25px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#f3f4f6",
                      width:'100%'
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "#374151", textAlign: "center" }}
                    >
                      This batch is already in your inventory. Would you like to restock this batch?
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddBoxIcon sx={{ color: "white" }} />}
                      sx={{
                        textTransform: "capitalize",
                        width: "25%",
                        marginTop: "10px",
                        background: "#1976d2",
                      }}
                      onClick={handleRestockMode}
                    >
                      Restock
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Units */}
            <Grid container style={{ width: "100%", justifyContent: "space-between" }}>
              <Grid item sx={{ width: {xs:'100%', md:'45%'} }}>
                <Typography component="label" sx={baseLabelSx}>
                  Units
                </Typography>
                <TextField
                  placeholder="Enter number of units"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={units}
                  onChange={(e) => setUnits(e.target.value)}
                  inputRef={refs.units}
                  helperText={
  tab === 0 ? (
    <>
      1 unit = 1 tablet, capsule, bottle, or vial.
      <br />
      Example: 1 strip of 10 tablets = 10 units.
      <br />
      Do NOT enter number of packs or boxes.
    </>
  ) : (
    <>
      1 unit = 1 item.
      <br />
      Example: 1 box with 50 gloves = 50 units.
      <br />
      Do NOT enter number of boxes or cartons.
    </>
  )
}
slotProps={{
    formHelperText: {
      sx: {
        mr: 0,
        ml: 0,   
        mb: 0,  
      },
    },
  }}
                  onKeyDown={(e) => {
    const needsExpiry = tab === 0 || trackExpiry; // medicine or product+trackExpiry
    handleEnterNext(e, needsExpiry ? refs.expiry : refs.supplier);
  }}
                  sx={sharedFieldSx}
                />
              </Grid>
            </Grid>

            {/* Expiry + Supplier */}
            <Grid container style={{ width: "100%", justifyContent: "space-between" }}>
              <Grid item sx={{ width: {xs:'100%', md:'45%'} }}>
                <Typography
                  component="label"
                  sx={{
                    ...baseLabelSx,
                    color: isDetailsLocked ? "darkgray" : "inherit",
                  }}
                >
                  Expiry Date {tab === 1 && !trackExpiry ? "(optional)" : ""}
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  disabled={isDetailsLocked || (tab === 1 && !trackExpiry)}
                  inputRef={refs.expiry}
                  onKeyDown={(e) => handleEnterNext(e, refs.supplier)}
                  sx={sharedFieldSx}
                />
              </Grid>

              <Grid item sx={{ width: {xs:'100%', md:'45%'} }}>
                <Typography
                  component="label"
                  sx={{
                    ...baseLabelSx,
                    color: isDetailsLocked ? "darkgray" : "inherit",
                  }}
                >
                  Supplier
                </Typography>
                <TextField
                  placeholder="Enter supplier name"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  disabled={isDetailsLocked}
                  inputRef={refs.supplier}
                  onKeyDown={(e) => {
  const nextRef = tab === 0 ? refs.strength : refs.wholesale;
  handleEnterNext(e, nextRef);
}}
                  sx={sharedFieldSx}
                />
              </Grid>
            </Grid>

            {/* Strength + Prices */}
            <Grid container style={{ width: "100%", justifyContent: "space-between" }}>
              <Grid item sx={{ width: {xs:'100%', md:'45%'} }}>
                <Typography
                  component="label"
                  sx={{
                    ...baseLabelSx,
                    color: isDetailsLocked ? "darkgray" : "inherit",
                  }}
                >
                  Strength {tab === 1 ? "(optional)" : ""}
                </Typography>
                <TextField
                  placeholder={tab === 0 ? "e.g. 20mg / 10ml" : "optional"}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={strength}
                  onChange={(e) => setStrength(e.target.value)}
                  disabled={isDetailsLocked}
                  inputRef={refs.strength}
                  onKeyDown={(e) => handleEnterNext(e, refs.wholesale)}
                  sx={sharedFieldSx}
                />
              </Grid>

              <Grid item sx={{ width: {xs:'100%', md:'45%'} }}>
                <Typography
                  component="label"
                  sx={{ ...baseLabelSx, color: isDetailsLocked ? "darkgray" : "inherit" }}
                >
                  Wholesale Price
                </Typography>
                <TextField
                  type="number"
                  placeholder="Enter wholesale price"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={wholesalePrice}
                  onChange={(e) => setWholesalePrice(e.target.value)}
                  disabled={isDetailsLocked}
                  inputRef={refs.wholesale}
                  onKeyDown={(e) => handleEnterNext(e, refs.retail)}
                  sx={sharedFieldSx}
                  slotProps={{
                    input: {
                      inputMode: "decimal",
                      step: "0.01",
                      min: 0,
                    },
                  }}
                />
              </Grid>

              <Grid container style={{ width: "100%", justifyContent: "space-between" }}>
                <Grid item sx={{ width: {xs:'100%', md:'45%'} }}>
                  <Typography
                    component="label"
                    sx={{ ...baseLabelSx, color: isDetailsLocked ? "darkgray" : "inherit" }}
                  >
                    Retail Price
                  </Typography>
                  <TextField
                    type="number"
                    placeholder="Enter retail price"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={retailPrice}
                    onChange={(e) => setRetailPrice(e.target.value)}
                    disabled={isDetailsLocked}
                    inputRef={refs.retail}
                    onKeyDown={(e) => handleEnterNext(e, refs.discount)}
                    sx={sharedFieldSx}
                    slotProps={{
                    input: {
                      inputMode: "decimal",
                      step: "0.01",
                      min: 0,
                    },
                  }}
                  />
                </Grid>

                <Grid item sx={{ width: {xs:'100%', md:'45%'} }}>
                  <Typography
                    component="label"
                    sx={{ ...baseLabelSx, color: isDetailsLocked ? "darkgray" : "inherit" }}
                  >
                    Discount
                  </Typography>
                  <TextField
                    placeholder="Enter discount %"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    disabled={isDetailsLocked}
                    inputRef={refs.discount}
                    onKeyDown={(e) => handleEnterNext(e, refs.lowThreshold)} 
                    sx={sharedFieldSx}
                  />
                </Grid>
              </Grid>
             
{/* 🆕 Stock alert thresholds */}
<Grid container sx={{ width: "100%", justifyContent: "space-between", mt: 2 }}>

  {/* Low stock */}
  <Grid item sx={{ width: { xs: "100%", md: "45%" } }}>
    <Typography
      component="label"
      sx={{ ...baseLabelSx, color: isDetailsLocked ? "darkgray" : "inherit" }}
    >
      Low stock alert
    </Typography>

    <RadioGroup
      value={lowMode}
      onChange={(e) => setLowMode(e.target.value)}
      sx={{ mt: 0.5 }}
    >
      <FormControlLabel
        style={{alignItems:'center'}}
        value="RECOMMENDED"
        disabled={isDetailsLocked}
        control={<Radio size="small" />}
        label={
          <Box>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Recommended
            </Typography>
            {hasUnitsNum && (
  <Typography
    variant="subtitle2"
    sx={{ fontWeight: 700, lineHeight: 1.2, color: "#6b7280" }}
  >
    {recommendedLowUnits}{" "}
    {recommendedLowUnits === 1 ? "unit" : "units"}
  </Typography>
)}

            {lowMode === "RECOMMENDED" && showLowPct && (
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#6b7280" }}
            >
              {`${lowIsApprox ? "≈" : ""}20% of ${q} units`}
            </Typography>
          )}


          </Box>
        }
        sx={{
          m: 0,
          mb: 0.5,
          alignItems: "flex-start",
          "& .MuiFormControlLabel-label": { width: "100%" },
        }}
      />

      <FormControlLabel
        value="CUSTOM"
        disabled={isDetailsLocked}
        control={<Radio size="small" />}
        label={
          <Box>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Custom
              {lowMode === "CUSTOM" && String(lowStockThreshold || "").trim()
                ? ` — ${lowStockThreshold} units`
                : ""}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "#6b7280" }}>
              Choose your own low-stock level
            </Typography>
          </Box>
        }
        sx={{
          m: 0,
          alignItems: "flex-start",
          "& .MuiFormControlLabel-label": { width: "100%" },
        }}
      />
    </RadioGroup>

    {lowMode === "CUSTOM" && (
  <TextField
    placeholder={hasUnitsNum ? `e.g. ${recommendedLowUnits}` : "e.g. 10"}
    fullWidth
    variant="outlined"
    size="small"
    value={lowStockThreshold}
    onChange={(e) => {
  const v = e.target.value;
  if (v === "") return setLowStockThreshold("");
  if (!/^\d+$/.test(v)) return;
  setLowStockThreshold(v);
}}
    disabled={isDetailsLocked}
    inputRef={refs.lowThreshold}
    onKeyDown={(e) => handleEnterNext(e, refs.criticalThreshold)}
    error={lowMinError}
    helperText={lowMinError ? "Minimum is 2 units." : " "}
    sx={{ ...sharedFieldSx, mt: 1 }}
    slotProps={{
    input: {
      inputMode: "numeric",
      step: 1,
      min: 2,
      pattern: "[0-9]*",
    },
  }}
  />
)}

  </Grid>

  {/* Critical */}
  <Grid item sx={{ width: { xs: "100%", md: "45%" }, mt: { xs: 2, md: 0 } }}>
    <Typography
      component="label"
      sx={{ ...baseLabelSx, color: isDetailsLocked ? "darkgray" : "inherit" }}
    >
      Critical alert
    </Typography>

    <RadioGroup
      value={critMode}
      onChange={(e) => setCritMode(e.target.value)}
      sx={{ mt: 0.5 }}
    >
      <FormControlLabel
      style={{alignItems:'center'}}
        value="RECOMMENDED"
        disabled={isDetailsLocked}
        control={<Radio size="small" />}
        label={
          <Box>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Recommended
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, color:'#6b7280' }}>
              {hasUnitsNum
  ? `${recommendedCritUnits} ${recommendedCritUnits === 1 ? "unit" : "units"}`
  : ""}
            </Typography>
            {critMode === "RECOMMENDED" && showCritPct && (
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#6b7280" }}
            >
              {`${critIsApprox ? "≈" : ""}10% of ${q} units`}
            </Typography>
          )}


          </Box>
        }
        sx={{
          m: 0,
          mb: 0.5,
          alignItems: "flex-start",
          "& .MuiFormControlLabel-label": { width: "100%" },
        }}
      />

      <FormControlLabel
        value="CUSTOM"
        disabled={isDetailsLocked}
        control={<Radio size="small" />}
        label={
          <Box>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Custom
              {critMode === "CUSTOM" && String(criticalThreshold || "").trim()
                ? ` — ${criticalThreshold} units`
                : ""}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "#6b7280" }}>
              Choose your own critical level
            </Typography>
          </Box>
        }
        sx={{
          m: 0,
          alignItems: "flex-start",
          "& .MuiFormControlLabel-label": { width: "100%" },
        }}
      />
    </RadioGroup>

    {critMode === "CUSTOM" && (
  <TextField
    placeholder={hasUnitsNum ? `e.g. ${recommendedCritUnits}` : "e.g. 3"}
    fullWidth
    variant="outlined"
    size="small"
    value={criticalThreshold}
    onChange={(e) => {
    const v = e.target.value;
    if (v === "") return setCriticalThreshold("");
    if (!/^\d+$/.test(v)) return;
    setCriticalThreshold(v);
  }}
    disabled={isDetailsLocked}
    inputRef={refs.criticalThreshold}
    onKeyDown={(e) => handleEnterNext(e, null)}
    error={critMinError || critVsLowError}
    helperText={
      critMinError
        ? "Minimum is 1 unit."
        : critVsLowError
        ? `Must be less than low stock alert value.`
        : " "
    }
    sx={{ ...sharedFieldSx, mt: 1 }}
    slotProps={{
    input: {
      inputMode: "numeric",
      step: 1,
      min: 1,
      pattern: "[0-9]*",
    },
  }}
  />
)}

  </Grid>

</Grid>




            </Grid>
          </Grid>
        </DialogContent>

        {errorMsg && (
          <Alert
            severity="error"
            sx={{ mb: 2, textTransform: "capitalize", color: "#db0000", marginBottom: "0" }}
            onClose={() => setErrorMsg("")}
          >
            <span style={{ fontWeight: "700" }}>Error: </span>
            {errorMsg}
          </Alert>
        )}

        <DialogActions sx={{
            flexDirection: { xs: 'column', sm:'row' },
          }}>
          {mode === "ADD_NEW" && isDisabled && (
            <Button
              size="small"
              color="inherit"
              startIcon={<HelpOutlineIcon fontSize="small" />}
              onClick={(e) => {
                if (showRequirements) {
                  setShowRequirements(false);
                  setRequirementsAnchorEl(null);
                } else {
                  setShowRequirements(true);
                  setRequirementsAnchorEl(e.currentTarget);
                }
              }}
              sx={{ textTransform: "none", mr: 1 }}
            >
              {showRequirements ? "Hide requirements" : "Why is Add Stock disabled?"}
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleAdd}
            startIcon={<AddIcon />}
            sx={{ textTransform: "none" }}
            disabled={isDisabled}
          >
            {mode === "RESTOCK" ? "Restock" : "Add Stock"}
          </Button>

          <Button
            onClick={handleClose}
            startIcon={<CloseIcon />}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        </DialogActions>

        {/* Popover with checklist */}
        <Popover
          open={requirementsOpen}
          anchorEl={requirementsAnchorEl}
          onClose={() => {
            setShowRequirements(false);
            setRequirementsAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "#f9fafb",
              border: "1px dashed #d1d5db",
              maxWidth: 320,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: "#374151" }}
            >
              To add stock, please check:
            </Typography>

            {validationRules.map((rule) => {
              const isPending = rule.active === false;
              const icon = isPending ? "•" : rule.ok ? "✓" : "✗";
              const color = isPending
                ? "#6b7280"
                : rule.ok
                ? "#15803d"
                : "#b91c1c";

              return (
                <Box
                  key={rule.key}
                  sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                >
                  <span
                    style={{
                      fontSize: 16,
                      marginRight: 8,
                    }}
                  >
                    {icon}
                  </span>
                  <Typography
                    variant="body2"
                    sx={{
                      color,
                      fontWeight: !isPending && rule.ok ? 500 : 400,
                    }}
                  >
                    {rule.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Popover>
      </BootstrapDialog>
    </React.Fragment>
  );
}
